import { useEffect } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, ArrowUpRight, Clock } from "lucide-react";
import Header from "@/components/site/Header";
import Footer from "@/components/site/Footer";
import { FadeUp } from "@/components/site/Reveal";
import { ARTICLES } from "@/data/articles";
import { PROJECTS } from "@/data/site";
import { usePageMeta } from "@/hooks/usePageMeta";

export default function Article() {
  const { slug } = useParams();
  const article = ARTICLES.find((a) => a.slug === slug);

  usePageMeta({ title: article?.seoTitle, description: article?.seoDescription });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!article) return <Navigate to="/conseils" replace />;

  return (
    <div className="relative min-h-screen bg-[var(--bg-primary)]" data-testid="article-page">
      <Header />
      <main className="relative pt-36 pb-28 overflow-hidden">
        <div className="glow-orb w-[460px] h-[460px] bg-blue-600/12 -top-24 -left-32" />
        <article className="max-w-3xl mx-auto px-6 md:px-10 relative">
          <FadeUp>
            <Link to="/conseils" className="inline-flex items-center gap-2 text-sm text-[var(--text-secondary)] hover:text-white transition-colors mb-10" data-testid="article-back-link">
              <ArrowLeft size={15} /> Tous les conseils
            </Link>
          </FadeUp>
          <FadeUp delay={0.05}>
            <div className="flex items-center gap-4 mb-5">
              <span className="font-mono-label text-[10px] tracking-[0.25em] uppercase text-[var(--accent-cyan)]">{article.category}</span>
              <span className="flex items-center gap-1.5 text-xs text-[var(--text-muted)]">
                <Clock size={12} /> {article.readTime} de lecture
              </span>
              <span className="text-xs text-[var(--text-muted)]">{article.date}</span>
            </div>
          </FadeUp>
          <FadeUp delay={0.1}>
            <h1 className="font-display font-extrabold text-4xl sm:text-5xl tracking-tight leading-[1.06]" data-testid="article-title">
              {article.title}
            </h1>
          </FadeUp>
          <FadeUp delay={0.16}>
            <p className="text-[var(--text-secondary)] text-lg mt-6 leading-relaxed">{article.excerpt}</p>
          </FadeUp>
          <FadeUp delay={0.22}>
            <div className="rounded-2xl overflow-hidden border border-white/[0.09] mt-10">
              <img src={article.image} alt={article.imageAlt} className="w-full aspect-[16/9] object-cover object-top" />
            </div>
          </FadeUp>

          {article.sections.map((s) => {
            const project = s.projectLink ? PROJECTS.find((p) => p.slug === s.projectLink) : null;
            return (
              <FadeUp key={s.h2} className="mt-14">
                <h2 className="font-display font-bold text-2xl md:text-3xl tracking-tight mb-5">{s.h2}</h2>
                <div className="text-[var(--text-secondary)] leading-relaxed space-y-4 text-base md:text-lg">
                  {s.paragraphs.map((p) => (
                    <p key={p.slice(0, 40)}>{p}</p>
                  ))}
                </div>
                {s.list && (
                  <ul className="mt-5 space-y-3">
                    {s.list.map((item) => (
                      <li key={item} className="flex items-start gap-3 text-base text-white/85">
                        <span className="mt-2 w-1.5 h-1.5 rounded-full bg-[var(--accent-cyan)] flex-shrink-0" />
                        {item}
                      </li>
                    ))}
                  </ul>
                )}
                {project && (
                  <Link
                    to={`/realisations/${project.slug}`}
                    className="group flex items-center gap-5 mt-7 rounded-2xl border border-white/[0.09] bg-[#0b0c1c] p-4 hover:border-cyan-400/30 transition-colors"
                    data-testid="article-project-link"
                  >
                    <img src={project.image} alt={project.alt} className="w-28 h-20 object-cover object-top rounded-xl flex-shrink-0" />
                    <div className="flex-1">
                      <p className="font-mono-label text-[9px] tracking-[0.2em] uppercase text-[var(--accent-cyan)] mb-1">{project.category}</p>
                      <p className="font-display font-bold">{project.shortName}</p>
                      <p className="text-xs text-[var(--text-muted)] mt-1">Voir cette réalisation</p>
                    </div>
                    <ArrowUpRight size={18} className="text-[var(--text-muted)] group-hover:text-[var(--accent-cyan)] transition-colors mr-2" />
                  </Link>
                )}
              </FadeUp>
            );
          })}

          <FadeUp className="mt-16">
            <div className="relative rounded-3xl border border-white/[0.09] bg-gradient-to-r from-blue-600/[0.1] via-violet-600/[0.08] to-transparent p-8 md:p-10 overflow-hidden">
              <h2 className="font-display font-bold text-2xl md:text-3xl tracking-tight">
                Envie de voir votre futur site <span className="text-gradient">avant de décider ?</span>
              </h2>
              <p className="text-[var(--text-secondary)] mt-3 leading-relaxed max-w-xl">
                Décrivez-nous votre métier : nous créons gratuitement un aperçu personnalisé de votre futur site
                internet, sans engagement. Pour aller plus loin, consultez notre{" "}
                <Link to="/creation-site-internet" className="text-[var(--accent-cyan)] hover:underline">
                  guide complet de la création de site internet
                </Link>
                .
              </p>
              <Link to="/demande" className="btn-gradient inline-flex items-center gap-2.5 rounded-full px-7 py-3.5 font-semibold text-white mt-7" data-testid="article-cta">
                Demander mon aperçu gratuit <ArrowRight size={16} />
              </Link>
            </div>
          </FadeUp>
        </article>
      </main>
      <Footer />
    </div>
  );
}
